import React, { FormEvent, useContext, useEffect, useState } from 'react';
import { Button, Dimmer, Form, Loader } from 'semantic-ui-react';
import './style.less';
import UserContext from '../../../contexts/UserContext';
import { updateResident } from '../../../services/family';
import sendToast from '../../../utils/Toast';
import { Registrant } from '../../types/Registrant';

const ResidentInfo = () => {

    const [specialHandling, setSpecialHandling] = useState<string>('');
    const [facilityRegisteredCell, setFacilityRegisteredCell] = useState<string>('');
    const [cell, setCell] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [firstName, setFirstName] = useState<string>('');
    const [lastName, setLastName] = useState<string>('');
    const [hideProfile, setHideProfile] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);

    const {
        userObject,
        setUser
    } = useContext(UserContext);

    const listOfResidents = (userObject && userObject.registrants && userObject.registrants.list) || [];
    const activeRegistrantsIndexes = (userObject && userObject.registrants && userObject.registrants.activeRegistrantsIndexes) || [];

    const activeResident = listOfResidents[activeRegistrantsIndexes[0]]; // get the first resident from the list of active residents for which the user is a relationship manager because neighbors web only supports the resident who is logged in and we can't deselect or change it
    const userHasRegistrants =
        userObject &&
            userObject.registrants &&
            userObject.registrants.list &&
            Array.isArray(userObject.registrants.list) &&
            userObject.registrants.list.length > 0
            ? true
            : false;
    const isRelationshipManager = userHasRegistrants && userObject.registrants.list.find((registrant) => registrant.manager) ? true : false;
    const allowEdit = isRelationshipManager && activeResident ? true : false;
    // Guests may have no phone on file, so Cell is only required for residents.
    const isGuestUser = userObject?.userType === 'guest';

    useEffect(() => {
        if (activeResident) {
            setSpecialHandling(activeResident.SpecialHandling || "");
            setFacilityRegisteredCell(activeResident.Cell || "");
            setHideProfile(activeResident.Private ? true : false);
            setCell(activeResident.SocialProfile?.Cell || '');
            setEmail(activeResident.SocialProfile?.Email || activeResident.Email || "");
            setFirstName(activeResident.SocialProfile?.FirstName || activeResident.FirstName || "");
            setLastName(activeResident.SocialProfile?.LastName || activeResident.LastName || "");

        }
    }, [userObject]);

    const handleInfoSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setLoading(true);
        try {
            if (allowEdit) {
                const residentId = activeResident._id;
                // The backend merges SocialProfile key by key, so the greeting message and type (edited on
                // the Greetings page) survive either way; the existing fields are still spread in so the
                // saved profile is sent back whole.
                const socialProfile: any = {
                    ...activeResident.SocialProfile,
                    Email: email,
                    FirstName: firstName,
                    LastName: lastName,
                    Cell: cell
                };

                // greetingEnabled is deliberately not sent: it follows the Daily Greetings
                // subscription, and the copy loaded at login can be stale.
                const residentPayload: Partial<Registrant> = {
                    SpecialHandling: specialHandling,
                    Private: hideProfile,
                    SocialProfile: socialProfile
                };
                if (facilityRegisteredCell) {
                    residentPayload.Cell = facilityRegisteredCell;
                }
                const updatedResidentRes = await updateResident(residentId, residentPayload);
                setUser(
                    {
                        ...userObject,
                        registrants: {
                            ...userObject.registrants,
                            list: userObject.registrants.list.map((resident: Registrant) => {
                                if (resident._id === residentId) {
                                return {
                                    ...resident,
                                    SpecialHandling: specialHandling,
                                    Private: hideProfile,
                                    SocialProfile: {
                                        ...resident.SocialProfile,
                                        Email: email,
                                        FirstName: firstName,
                                        LastName: lastName,
                                        Cell: cell
                                    } as any,
                                    Cell: facilityRegisteredCell
                                };
                                }
                                return resident;
                            })
                        }
                    }
                );
                if (!updatedResidentRes) {
                    throw new Error('Error updating profile');
                }
                sendToast('success', 'Profile updated successfully');
            }
        } catch (error) {
            sendToast('error', error instanceof Error ? error.message : 'Error updating profile');
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Dimmer active={loading} inverted>
                <Loader style={{ margin: 'auto' }} active={loading}>Loading</Loader>
            </Dimmer>
            <div className="sect">
                <div className="sect-header">
                    <div className="sect-icon">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
                    </div>
                    <div className="sect-title">About Me</div>
                </div>
                <div className="sect-body">
                    <Form onSubmit={(e) => handleInfoSubmit(e)}>
                        <div className="field-group">
                            <div className="field-row-2">
                                <div className="field">
                                    <Form.Input
                                        required={true}
                                        value={firstName}
                                        placeholder="First name"
                                        onChange={(e) => setFirstName(e.target.value)}
                                        label="First name"
                                        readOnly={allowEdit ? false : true}
                                    />
                                </div>
                                <div className="field">
                                    <Form.Input
                                        required={true}
                                        value={lastName}
                                        placeholder="Last name"
                                        onChange={(e) => setLastName(e.target.value)}
                                        label="Last name"
                                        readOnly={allowEdit ? false : true}
                                    />
                                </div>
                            </div>

                            <div className={allowEdit && facilityRegisteredCell ? 'field-row-3' : 'field-row-2'}>
                                <div className="field">
                                    <Form.Input
                                        value={cell}
                                        placeholder="Cell"
                                        onChange={(e) => setCell(e.target.value)}
                                        label="Cell"
                                        required={!isGuestUser}
                                        readOnly={allowEdit ? false : true}
                                    />
                                </div>
                                {allowEdit && facilityRegisteredCell && (
                                    <div className="field">
                                        <Form.Input
                                            value={facilityRegisteredCell}
                                            placeholder="Registered cell"
                                            onChange={(e) => setFacilityRegisteredCell(e.target.value)}
                                            label="Registered cell"
                                        />
                                    </div>
                                )}
                                <div className="field">
                                    <Form.Input
                                        value={email}
                                        placeholder="Email"
                                        onChange={(e) => setEmail(e.target.value)}
                                        type={'email'}
                                        label="Email"
                                        readOnly={allowEdit ? false : true}
                                    />
                                </div>
                            </div>

                            {allowEdit && (
                                <div className="field">
                                    <Form.TextArea
                                        value={specialHandling}
                                        placeholder="Tell the community about yourself…"
                                        onChange={(e: any) => setSpecialHandling(e.target.value)}
                                        label="About Me"
                                    />
                                </div>
                            )}

                            {activeResident && allowEdit && (
                                <>
                                    <div className="divider" />
                                    <div className="toggle-row">
                                        <div className="toggle-wrap">
                                            <label className="toggle">
                                                <input
                                                    type="checkbox"
                                                    checked={hideProfile}
                                                    onChange={(e) => setHideProfile(e.target.checked)}
                                                />
                                                <span className="toggle-track" />
                                            </label>
                                            <span className="toggle-label">Hide my details</span>
                                        </div>
                                    </div>
                                </>
                            )}

                            {allowEdit && (
                                <div className="save-row">
                                    <Button
                                        className="btn-save"
                                        type="submit"
                                    >
                                        Save Changes
                                    </Button>
                                </div>
                            )}
                        </div>
                    </Form>
                </div>
            </div>
        </>
    );
};

export default ResidentInfo;
